<template>
  <div class="playground">
    <p class="playground-context">
      This example uses trip planning to show the engine’s logic. Trip answers represent product specifications,
      rule columns represent business scenarios, and packing items represent the required screens and fields.
    </p>
    <section class="stage">
      <header class="stage-head"><span class="stage-num">1</span> Tell us about the trip</header>
      <div class="qform">
        <label v-for="f in visible" :key="f.sno" class="qrow" :class="{ 'is-sub': f.parent }">
          <span class="qlabel">
            <svg v-if="f.parent" class="sub-arrow" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M4 2v8h8m-3-3 3 3-3 3" fill="none" stroke="currentColor" stroke-width="1.4" /></svg>{{ f.name }}
          </span>
          <select v-model="answers[f.sno]" class="qselect">
            <option v-for="p in f.params" :key="p" :value="p">{{ p }}</option>
          </select>
        </label>
      </div>
      <p class="stage-note" v-if="hasSub">
        Your answer reveals a follow-up question.
      </p>
    </section>

    <section class="stage">
      <header class="stage-head"><span class="stage-num">2</span> Follow the matching rules</header>
      <p class="stage-note stage-note--top">
        Each table represents one decision. A column remains eligible only when all of its conditions match the
        answers. If several columns match, the most specific column on the right takes priority. ALL acts as a
        wildcard.
      </p>

      <div v-for="group in ev.groups" :key="group.cfg.id" class="cfg">
        <div class="cfg-head">
          <span class="cfg-name">{{ group.cfg.name }}</span>
          <span class="cfg-blurb">{{ group.cfg.blurb }}</span>
        </div>
        <div class="table-scroll">
          <table class="rules">
            <thead>
              <tr>
                <th class="rowhead"></th>
                <th
                  v-for="(s, j) in group.cfg.scenarios"
                  :key="s.name"
                  class="schead"
                  :class="{ matched: group.results[j].match, winning: group.winning === j }"
                >{{ s.name }}</th>
              </tr>
            </thead>
            <tbody>
              <tr class="group-row"><td :colspan="group.cfg.scenarios.length + 1">Your answers (inputs)</td></tr>
              <tr v-for="sno in group.cfg.inputs" :key="sno" :class="{ dim: !ev.visible.has(sno) }">
                <td class="rowhead">{{ fname(sno) }}</td>
                <td
                  v-for="(s, j) in group.cfg.scenarios"
                  :key="s.name"
                  class="cell spec-cell"
                  :class="{ colmatch: group.results[j].match, winning: group.winning === j, bad: !group.results[j].cell[sno].ok }"
                >{{ group.results[j].cell[sno].req }}</td>
              </tr>
              <tr class="group-row"><td :colspan="group.cfg.scenarios.length + 1">What to pack (outputs)</td></tr>
              <tr v-for="id in group.cfg.items" :key="id">
                <td class="rowhead">{{ iname(id) }}</td>
                <td
                  v-for="(s, j) in group.cfg.scenarios"
                  :key="s.name"
                  class="cell comp-cell"
                  :class="[ s.out[id] === 'Y' ? 'yes' : 'no', { colmatch: group.results[j].match, winning: group.winning === j } ]"
                >{{ s.out[id] }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="cfg-note">
          <template v-if="matchCount(group) > 1">
            Your answers match {{ matchCount(group) }} columns. The most specific,
            <strong>“{{ winnerName(group) }}”</strong>, wins.
          </template>
          <template v-else>
            Your answers land on <strong>“{{ winnerName(group) }}”</strong>.
          </template>
        </p>
      </div>
    </section>

    <section class="stage">
      <header class="stage-head"><span class="stage-num">3</span> Your packing list</header>
      <div class="phone-wrap">
        <div class="phone">
          <div class="phone-notch"></div>
          <div class="phone-screen">
            <div class="app-bar">
              <span>Packing list</span>
              <span class="app-count">{{ ev.shown.length }}</span>
            </div>
            <div class="app-body">
              <div v-for="c in ev.shown" :key="c.id" class="app-card">
                <span class="app-check" aria-hidden="true"></span>
                <span class="app-card-text">
                  <span class="app-card-title">{{ c.name }}</span>
                  <span class="app-card-hint">{{ c.hint }}</span>
                </span>
              </div>
              <div v-if="!ev.shown.length" class="app-empty">
                Just the essentials for this trip.
              </div>
            </div>
          </div>
        </div>
        <div class="phone-side">
          <p class="phone-caption">
            Each table contributes items to the list. The filled dots show which outputs were selected.
          </p>
          <div v-for="group in ev.groups" :key="group.cfg.id" class="legend-group">
            <span class="legend-title">{{ group.cfg.name }}</span>
            <ul class="comp-legend">
              <li v-for="id in group.cfg.items" :key="id" :class="ev.out[id] === 'Y' ? 'on' : 'off'">
                <span class="dot"></span>{{ iname(id) }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { fields, components, configurators, visibleFields, evaluate, defaultAnswers, fieldBySno, componentById } from '../../data/playbookDemo';

export default {
  name: 'InsuranceRuleEngine',
  data() {
    return {
      fields,
      components,
      configurators,
      answers: defaultAnswers(),
    };
  },
  computed: {
    visible() { return visibleFields(this.answers); },
    hasSub() { return this.visible.some((f) => f.parent); },
    ev() { return evaluate(this.answers); },
  },
  methods: {
    fname(sno) { const f = fieldBySno(sno); return f ? f.name : sno; },
    iname(id) { const c = componentById(id); return c ? c.name : id; },
    matchCount(group) { return group.results.filter((r) => r.match).length; },
    winnerName(group) { return group.winning >= 0 ? group.cfg.scenarios[group.winning].name : 'No matching rule'; },
  },
};
</script>

<style scoped>
.playground {
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  padding: 4px 18px 18px;
  margin: 1.4rem 0;
  background: rgba(0, 0, 0, 0.012);
}
.playground-context {
  margin: 0;
  padding: 14px 0 2px;
  color: #666;
  font-size: 0.88rem;
  line-height: 1.55;
}
.stage { padding: 18px 0; border-bottom: 1px solid #ededed; }
.stage:last-child { border-bottom: none; }
.stage-head {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.98rem;
  color: #333;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}
.stage-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #333;
  color: #fff;
  font-size: 0.78rem;
  margin-right: 9px;
  flex-shrink: 0;
}
.stage-note { font-size: 0.82rem; line-height: 1.55; margin-top: 12px; color: #777; }
.stage-note--top { margin-top: 0; margin-bottom: 18px; }

.qform { display: flex; flex-direction: column; gap: 10px; max-width: 400px; }
.qrow { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.qrow.is-sub { padding-left: 14px; }
.qlabel { font-size: 0.9em; color: #444; }
.sub-arrow { color: #aaa; margin-right: 5px; }
.qselect {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.85rem;
  padding: 5px 9px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: #fff;
  color: #222;
  min-width: 168px;
  cursor: pointer;
}

.cfg { margin-bottom: 22px; }
.cfg:last-child { margin-bottom: 0; }
.cfg-head { display: flex; align-items: baseline; gap: 0.6em; margin-bottom: 7px; flex-wrap: wrap; }
.cfg-name { font-family: "Quattrocento Sans", sans-serif; font-size: 0.86rem; font-weight: 700; color: #2b2b2b; }
.cfg-blurb { font-size: 0.78rem; color: #999; font-style: italic; }
.cfg-note { font-size: 0.8rem; color: #555; margin-top: 8px; }

.table-scroll { overflow-x: auto; }
table.rules { border-collapse: collapse; width: 100%; font-family: "Quattrocento Sans", sans-serif; }
table.rules th, table.rules td {
  border: 1px solid #e4e4e4;
  padding: 6px 9px;
  font-size: 0.74rem;
  text-align: center;
  white-space: nowrap;
}
.rowhead { text-align: left !important; color: #555; font-weight: normal; background: rgba(0,0,0,0.015); }
.schead { color: #777; font-weight: normal; transition: background 0.2s, color 0.2s; }
.schead.matched { color: #333; background: rgba(0, 0, 0, 0.04); }
.schead.winning { background: #333; color: #fff; font-weight: bold; }
.group-row td {
  text-align: left !important;
  font-size: 0.64rem !important;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #aaa;
  background: rgba(0,0,0,0.02);
  border-left: none;
  border-right: none;
}
tr.dim { opacity: 0.38; }
.cell { transition: background 0.2s, color 0.2s; color: #888; }
.cell.colmatch { background: rgba(0, 0, 0, 0.035); }
.cell.winning { background: rgba(0, 0, 0, 0.075); }
.spec-cell { color: #555; }
.spec-cell.bad { color: #999; text-decoration: line-through; }
.comp-cell.yes { color: #333; font-weight: bold; }
.comp-cell.no { color: #888; }

.phone-wrap { display: flex; gap: 24px; align-items: flex-start; flex-wrap: wrap; }
.phone {
  position: relative;
  width: 210px;
  flex-shrink: 0;
  border: 2px solid #333;
  border-radius: 24px;
  padding: 15px 9px 14px;
  background: #fff;
}
.phone-notch {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 5px;
  border-radius: 3px;
  background: #333;
}
.phone-screen { margin-top: 8px; }
.app-bar {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.82rem;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 9px;
  margin-bottom: 9px;
  border-bottom: 1px solid #eee;
}
.app-count {
  font-size: 0.68rem;
  color: #fff;
  background: #333;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
}
.app-body { display: flex; flex-direction: column; gap: 7px; min-height: 168px; }
.app-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: rgba(0,0,0,0.015);
}
.app-check {
  flex-shrink: 0;
  margin-top: 1px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #333;
  position: relative;
}
.app-check::after {
  content: '';
  width: 6px;
  height: 3px;
  border-left: 1.5px solid #fff;
  border-bottom: 1.5px solid #fff;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -65%) rotate(-45deg);
}
.app-card-text { display: flex; flex-direction: column; }
.app-card-title { font-family: "Quattrocento Sans", sans-serif; font-size: 0.78rem; color: #2b2b2b; line-height: 1.2; }
.app-card-hint { font-size: 0.66rem; color: #999; margin-top: 2px; }
.app-empty { font-size: 0.74rem; color: #aaa; text-align: center; padding-top: 60px; }
.phone-side { flex: 1 1 220px; }
.phone-caption { font-size: 0.85em; color: #666; margin-top: 4px; }
.legend-group { margin-top: 14px; }
.legend-title {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.66rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #aaa;
}
.comp-legend { list-style: none; padding: 0; margin: 4px 0 0; }
.comp-legend li {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  padding: 2px 0;
  transition: color 0.2s;
}
.comp-legend .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 9px;
  background: #d2d2d2;
  transition: background 0.2s;
}
.comp-legend li.on { color: #2b2b2b; }
.comp-legend li.on .dot { background: #333; }
.comp-legend li.off { color: #bbb; }

body.dark-mode .stage-note { color: var(--site-text-secondary); }
body.dark-mode .playground-context { color: var(--site-text-secondary); }
body.dark-mode .playground { border-color: #2a2a2a; background: rgba(255,255,255,0.015); }
body.dark-mode .stage { border-bottom-color: #242424; }
body.dark-mode .stage-head { color: #cfcfcf; }
body.dark-mode .stage-num { background: #cfcfcf; color: #000; }
body.dark-mode .qlabel { color: #c4c4c4; }
body.dark-mode .qselect { background: #1a1a1a; color: #ddd; border-color: #3a3a3a; }
body.dark-mode .cfg-name { color: #e0e0e0; }
body.dark-mode .cfg-blurb { color: var(--site-text-tertiary); }
body.dark-mode .cfg-note { color: var(--site-text-secondary); }
body.dark-mode table.rules th, body.dark-mode table.rules td { border-color: #2a2a2a; }
body.dark-mode .rowhead { color: var(--site-text-secondary); background: rgba(255,255,255,0.02); }
body.dark-mode .schead.matched { color: #ddd; background: rgba(255,255,255,0.05); }
body.dark-mode .schead.winning { background: #dedede; color: #202020; }
body.dark-mode .group-row td { color: var(--site-text-tertiary); background: rgba(255,255,255,0.025); }
body.dark-mode .cell { color: var(--site-text-tertiary); }
body.dark-mode .cell.colmatch { background: rgba(255,255,255,0.04); }
body.dark-mode .cell.winning { background: rgba(255,255,255,0.10); }
body.dark-mode .spec-cell { color: #b0b0b0; }
body.dark-mode .comp-cell.yes { color: #e6e6e6; }
body.dark-mode .comp-cell.no { color: #aaa; }
body.dark-mode .phone { border-color: #cfcfcf; background: #161616; }
body.dark-mode .phone-notch { background: #cfcfcf; }
body.dark-mode .app-bar { color: #ddd; border-bottom-color: #2a2a2a; }
body.dark-mode .app-count { background: #cfcfcf; color: #161616; }
body.dark-mode .app-card { border-color: #2e2e2e; background: rgba(255,255,255,0.02); }
body.dark-mode .app-check { background: #dedede; }
body.dark-mode .app-check::after { border-color: #202020; }
body.dark-mode .app-card-title { color: #ddd; }
body.dark-mode .phone-caption { color: var(--site-text-secondary); }
body.dark-mode .legend-title { color: var(--site-text-tertiary); }
body.dark-mode .comp-legend li.on { color: #e6e6e6; }
body.dark-mode .comp-legend li.on .dot { background: #dedede; }

@media (max-width: 560px) {
  .qrow { flex-direction: column; align-items: flex-start; gap: 4px; }
  .phone-wrap { justify-content: center; }
}

@container (max-width: 560px) {
  .qrow { flex-direction: column; align-items: flex-start; gap: 4px; }
  .phone-wrap { justify-content: center; }
}
</style>
